import EventIcon from '@mui/icons-material/Event';
import { IconButton } from '@mui/material';
import { DateRangePicker as DateRangePickerMui, SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { formatISO, isValid, parseISO } from 'date-fns';
import React from 'react';
/**
 * A component for selecting a date range (start and end date) using Material UI's date picker.
 *
 * @component
 *
 * @param {DateRangePickerProps} props - Props for the DateRangePicker component.
 * @param {SxProps<Theme>} [props.sx] - Optional custom styles applied to the root component via the `sx` prop.
 * @param {string} props.label - Label for the input field.
 * @param {string} [props.minDate] - The minimum selectable date (in ISO format).
 * @param {string} [props.maxDate] - The maximum selectable date (in ISO format).
 * @param {boolean} [props.disabled] - Whether the input is disabled.
 * @param {boolean} [props.readOnly] - Whether the input is read-only.
 * @param {boolean} [props.required] - Whether the input is required.
 * @param {boolean} [props.error] - Whether the input is in an error state.
 * @param {boolean} [props.fullWidth] - If true, the input field will take the full width of its container.
 * @param {number} [props.tabIndex] - Sets the tab order of the input.
 * @param {DateRangePickerValue} props.value - Current value of the date range (start and end ISO strings).
 * @param {boolean} [props.hideLabel] - If true, visually hides the label while keeping it accessible.
 * @param {(newValue: DateRangePickerValue) => void} props.onChange - Callback fired when the value changes.
 * @param {string} [props.'data-test'] - Optional test ID used for test automation.
 * @param {boolean} [props.autoFocus] - Whether the field should auto-focus on mount.
 * @param {string} [props.ariaDescribedBy] - ARIA attribute for accessibility describing the field.
 *
 * @returns {React.ReactElement} Rendered date range picker component.
 */
export const DateRangePicker = (props) => {
    var _a;
    const [focused, setFocused] = React.useState(false);
    const hasValue = !!props.value.from || !!props.value.to;
    const handleChange = (newValue) => {
        props.onChange({
            from: newValue[0] && isValid(newValue[0]) ? formatISO(newValue[0], { representation: 'date' }) : undefined,
            to: newValue[1] && isValid(newValue[1]) ? formatISO(newValue[1], { representation: 'date' }) : undefined,
        });
    };
    const parsedValue = [
        props.value.from ? parseISO(props.value.from) : null,
        props.value.to ? parseISO(props.value.to) : null,
    ];
    return (React.createElement(DateRangePickerMui, { slots: { field: SingleInputDateRangeField }, slotProps: {
            textField: {
                onFocus: () => {
                    setFocused(true);
                },
                onBlur: () => {
                    setFocused(false);
                },
                tabIndex: props.tabIndex,
                required: props.required,
                error: props.error,
                InputLabelProps: !props.hideLabel ? undefined : { shrink: false },
                inputProps: { 'aria-label': props.label, 'aria-describedby': props.ariaDescribedBy },
                InputProps: {
                    endAdornment: (React.createElement(IconButton, { "aria-label": props.label },
                        React.createElement(EventIcon, null))),
                    sx: {
                        paddingRight: 0,
                    },
                },
            },
        }, value: parsedValue, onChange: handleChange, label: (hasValue || focused) && props.hideLabel ? '' : props.label, sx: [
            {
                width: props.fullWidth ? '100%' : 260,
                '& label.MuiInputLabel-root': { paddingRight: 3 },
            },
            ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        ], disabled: props.disabled, readOnly: props.readOnly, autoFocus: (_a = props.autoFocus) !== null && _a !== void 0 ? _a : false, maxDate: props.maxDate ? parseISO(props.maxDate) : undefined, minDate: props.minDate ? parseISO(props.minDate) : undefined }));
};
